import React, { useEffect, useRef } from 'react';
import { themeSelectorStyles } from './style';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import { Theme } from '../PhaserApp/enums/Themes';
import Tooltip from '../Tooltip/Tooltip';

const SPRITE_FRAME_SIZE = 512;

interface ThemeSelectorProps {
  size?: 'small' | 'large';
}

type TintGradient = { from: string; to: string };

const SpriteIcon: React.FC<{ src: string; size?: number; tint?: TintGradient; overlaySrc?: string }> = ({
  src,
  size = 20,
  tint,
  overlaySrc,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const render = (baseImg: HTMLImageElement, overlayImg?: HTMLImageElement) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, size, size);
      ctx.globalCompositeOperation = 'source-over';
      // draw first frame (0,0, frame) of the body sheet
      ctx.drawImage(baseImg, 0, 0, SPRITE_FRAME_SIZE, SPRITE_FRAME_SIZE, 0, 0, size, size);

      if (tint) {
        // Multiply blend keeps the body's highlights/shading while infusing color.
        ctx.globalCompositeOperation = 'multiply';
        const grad = ctx.createLinearGradient(0, 0, 0, size);
        grad.addColorStop(0, tint.from);
        grad.addColorStop(1, tint.to);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, size, size);
        // Re-mask to the body's alpha so transparent pixels stay transparent.
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(baseImg, 0, 0, SPRITE_FRAME_SIZE, SPRITE_FRAME_SIZE, 0, 0, size, size);
        ctx.globalCompositeOperation = 'source-over';
      }

      // The fixed overlay (e.g. the penguin's face) sits on top and keeps its own colours.
      if (overlayImg) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(overlayImg, 0, 0, SPRITE_FRAME_SIZE, SPRITE_FRAME_SIZE, 0, 0, size, size);
      }
    };

    const baseImg = new Image();
    let overlayImg: HTMLImageElement | undefined;
    let pending = overlaySrc ? 2 : 1;
    const onReady = () => {
      if (cancelled) return;
      pending -= 1;
      if (pending === 0) render(baseImg, overlayImg);
    };

    baseImg.onload = onReady;
    baseImg.src = src;

    if (overlaySrc) {
      overlayImg = new Image();
      overlayImg.onload = onReady;
      overlayImg.src = overlaySrc;
    }

    return () => {
      cancelled = true;
    };
  }, [src, size, tint?.from, tint?.to, overlaySrc]);

  return <canvas className="icon" ref={canvasRef} width={size} height={size} style={{ width: size, height: size }} />;
};

const themeOptions: {
  key: string;
  label: string;
  sprite: string;
  value: Theme;
  tint?: TintGradient;
  overlaySprite?: string;
}[] = [
  {
    key: 'default',
    label: 'default',
    sprite: 'assets/phaser/images/Silver_16_flat_8192x512_3.png',
    value: Theme.Default,
    tint: { from: '#9DB7D9', to: '#5C7AA3' },
  },
  {
    key: 'child',
    label: 'child',
    // Body sheet (tinted) + fixed face sheet drawn on top, matching the in-game child target.
    sprite: 'assets/phaser/images/penguin_owl_tint_8192x512.png',
    overlaySprite: 'assets/phaser/images/penguin_owl_fixed_8192x512.png',
    value: Theme.Child,
    tint: { from: '#A7CBF0', to: '#6E9FD4' },
  },
];

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ size = 'small' }) => {
  const { t } = useTranslation();
  const { theme, setTheme } = usePractitionerControlsContext();

  const selectedKey: string = theme === Theme.Child ? 'child' : 'default';
  const iconSize = size === 'large' ? 32 : 24;

  const handleSelect = (event: SelectChangeEvent<string>) => {
    const key = event.target.value;
    setTheme(key === 'child' ? Theme.Child : Theme.Default);
  };

  return (
    <Tooltip title={t('theme') as string} placement={'left'}>
      <FormControl variant="standard" sx={{ minWidth: 'auto' }}>
        <Select
          variant="standard"
          sx={size === 'large' ? themeSelectorStyles.pickerLarge : themeSelectorStyles.picker}
          value={selectedKey}
          onChange={handleSelect}
          input={<Input disableUnderline />}
          renderValue={() => {
            const item = themeOptions.find((o) => o.key === selectedKey)!;
            return <SpriteIcon src={item.sprite} size={iconSize} tint={item.tint} overlaySrc={item.overlaySprite} />;
          }}
          MenuProps={{ PaperProps: { sx: themeSelectorStyles.menuPaper } }}
        >
          {themeOptions.map((option) => (
            <MenuItem value={option.key} key={option.key}>
              <SpriteIcon src={option.sprite} size={24} tint={option.tint} overlaySrc={option.overlaySprite} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
};

export default ThemeSelector;
